import { Spinner } from "@scoach/ui";
import { Check, Close, Plus, Spark } from "@scoach/ui/icons";
import { useState } from "react";

/**
 * Reusable meeting labels/tags editor. Renders the current labels as removable chips,
 * lets the user add labels manually (Enter or comma), and — when a `suggest` function
 * is provided — fetches model-suggested labels the user can approve one-by-one or all
 * at once. The parent owns persistence: every change calls `onChange` with the full new
 * label list. Case-insensitive de-dupe; blanks ignored.
 */
export function MeetingLabels({
  labels,
  onChange,
  suggest,
  readOnly = false,
  compact = false,
}: {
  labels: string[];
  onChange: (next: string[]) => void;
  /** Fetches suggested labels (already excluding ones applied). Omit to hide the button. */
  suggest?: () => Promise<string[]>;
  readOnly?: boolean;
  compact?: boolean;
}) {
  const [input, setInput] = useState("");
  const [suggested, setSuggested] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const has = (l: string) => labels.some((x) => x.toLowerCase() === l.trim().toLowerCase());

  function add(raw: string) {
    const label = raw.trim().replace(/\s+/g, " ").slice(0, 40);
    if (!label || has(label)) return;
    onChange([...labels, label]);
    setSuggested((prev) => prev.filter((s) => s.toLowerCase() !== label.toLowerCase()));
  }

  function remove(label: string) {
    onChange(labels.filter((l) => l !== label));
  }

  function commitInput() {
    // Support comma-separated bulk entry.
    for (const part of input.split(",")) add(part);
    setInput("");
  }

  async function runSuggest() {
    if (!suggest || loading) return;
    setLoading(true);
    setError("");
    try {
      const out = await suggest();
      const fresh = out.filter((s) => !has(s));
      setSuggested(fresh);
      if (fresh.length === 0) setError("No new labels to suggest.");
    } catch {
      setError("Couldn't suggest labels. Try again or add your own.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="meeting-labels" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
        {!compact && (
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)", marginRight: 2 }}>
            Labels
          </span>
        )}
        {labels.map((l) => (
          <span key={l} className="mtg-label-chip">
            {l}
            {!readOnly && (
              <button
                type="button"
                className="mtg-label-x"
                aria-label={`Remove label ${l}`}
                onClick={() => remove(l)}
              >
                <Close size={11} />
              </button>
            )}
          </span>
        ))}
        {labels.length === 0 && (
          <span style={{ fontSize: 12.5, color: "var(--text-4)" }}>
            {readOnly ? "No labels" : "No labels yet — add one or get suggestions"}
          </span>
        )}
      </div>

      {!readOnly && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
          <input
            type="text"
            className="setup-input"
            style={{ width: compact ? 180 : 220, padding: "5px 9px", fontSize: 13 }}
            value={input}
            placeholder="Add a label…"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                commitInput();
              }
            }}
            onBlur={commitInput}
          />
          <button
            type="button"
            className="ghost-btn xs"
            onClick={commitInput}
            disabled={!input.trim()}
            title="Add label"
          >
            <Plus size={13} /> Add
          </button>
          {suggest && (
            <button
              type="button"
              className="ghost-btn xs"
              onClick={() => void runSuggest()}
              disabled={loading}
              title="Suggest labels from this meeting"
            >
              {loading ? <Spinner size={12} /> : <Spark size={13} />} Suggest
            </button>
          )}
        </div>
      )}

      {suggested.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--text-4)" }}>Suggested:</span>
          {suggested.map((s) => (
            <button
              key={s}
              type="button"
              className="mtg-label-suggest"
              onClick={() => add(s)}
              title="Add this label"
            >
              <Plus size={11} /> {s}
            </button>
          ))}
          <button
            type="button"
            className="ghost-btn xs"
            onClick={() => {
              for (const s of suggested) add(s);
            }}
          >
            <Check size={12} /> Add all
          </button>
        </div>
      )}

      {error && <div style={{ fontSize: 12, color: "var(--text-4)" }}>{error}</div>}
    </div>
  );
}
